import React from 'react';
import { AlertTriangle, Minus, Plus, Trash2 } from 'lucide-react';
import { Product } from '../types/cart.types';
import { formatCurrency } from '../../../lib/formatters';
import {
  CART_MAX_QUANTITY_PER_ITEM,
  isHeavyProduct,
  isBulkyProduct,
  resolveTemperatureBadge,
  type TemperaturePreference
} from '../utils/cartNormalization';
import BeverageTemperatureControl from './BeverageTemperatureControl';

export interface CartLineItem {
  product: Product | null;
  unavailable: boolean;
  catalogPending: boolean;
  /** Compound line key (`productId:temp`) for remove / qty / temp. */
  lineId: string;
  /** Catalog productId (no temp suffix). */
  productId: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  temperaturePreference: TemperaturePreference;
  isBeverage: boolean;
}

export interface CartLineListProps {
  catalogPendingLineItems: CartLineItem[];
  unavailableLineItems: CartLineItem[];
  availableLineItems: CartLineItem[];
  hasOutOfStockItems: boolean;
  hasUnavailableItems: boolean;
  suggestedProducts: Product[];
  onRemoveOutOfStockItems: () => void;
  onRemoveUnavailableItems: () => void;
  onClearAllItems?: () => void;
  onRemoveItem: (lineId: string) => void;
  onUpdateQuantity: (lineId: string, nextQty: number, maxStock?: number) => void;
  onTemperatureChange: (lineId: string, next: TemperaturePreference) => void;
  onAddSuggestion: (prod: Product) => void;
}

const CartLineList: React.FC<CartLineListProps> = ({
  catalogPendingLineItems,
  unavailableLineItems,
  availableLineItems,
  hasOutOfStockItems,
  hasUnavailableItems,
  suggestedProducts,
  onRemoveOutOfStockItems,
  onRemoveUnavailableItems,
  onClearAllItems,
  onRemoveItem,
  onUpdateQuantity,
  onTemperatureChange,
  onAddSuggestion,
}) => {
  return (
    <>
      {hasOutOfStockItems && (
        <div className="flex items-center justify-between gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-2xl animate-in fade-in duration-200">
          <div className="flex items-center gap-2.5 min-w-0">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
            <div>
              <p className="text-xs font-black uppercase tracking-wider text-amber-300">
                Some items are out of stock
              </p>
              <p className="text-[10px] text-amber-200/80 font-medium">
                Remove them to proceed to checkout
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onRemoveOutOfStockItems}
            className="shrink-0 px-3 py-1.5 bg-amber-400 text-black rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-amber-300 active:scale-95 transition-all"
          >
            Remove All
          </button>
        </div>
      )}

      <div className="space-y-3">
        {catalogPendingLineItems.length > 0 ? (
          <div className="p-4 rounded-3xl border border-white/10 bg-white/5 text-center">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
              Matching cart to catalog…
            </p>
          </div>
        ) : null}
        {hasUnavailableItems ? (
          <div className="p-4 rounded-3xl border border-amber-500/30 bg-amber-500/5 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-amber-300">
                  Unavailable in catalog
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  {unavailableLineItems.length} item{unavailableLineItems.length === 1 ? '' : 's'} no longer match live products.
                </p>
              </div>
              <button
                type="button"
                onClick={onRemoveUnavailableItems}
                className="shrink-0 px-3 py-1.5 rounded-xl border border-amber-400/40 text-amber-200 text-[10px] font-black uppercase tracking-wider hover:bg-amber-500/10"
              >
                Remove all
              </button>
            </div>
            {unavailableLineItems.map((li) => (
              <div
                key={li.lineId}
                className="flex items-center justify-between gap-3 p-3 rounded-2xl bg-black/40 border border-white/5"
              >
                <div className="min-w-0">
                  <p className="text-xs font-bold text-white truncate">Unavailable item</p>
                  <p className="text-[10px] text-slate-500 font-mono truncate">{li.productId}</p>
                </div>
                <button
                  type="button"
                  onClick={() => onRemoveItem(li.lineId)}
                  className="text-[10px] font-black uppercase tracking-wider text-amber-300 hover:text-white"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        ) : null}
        {availableLineItems.length > 0 && onClearAllItems ? (
          <div className="flex items-center justify-between px-2 pt-1 pb-1">
            <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 font-mono">
              Active Cart Items ({availableLineItems.reduce((sum, item) => sum + item.quantity, 0)})
            </span>
            <button
              type="button"
              onClick={onClearAllItems}
              className="text-[9px] font-black uppercase tracking-widest text-ninpo-red hover:underline flex items-center gap-1.5 transition-opacity hover:opacity-80 font-mono"
            >
              <Trash2 className="w-3 h-3" />
              Clear Cart
            </button>
          </div>
        ) : null}
        {availableLineItems.length === 0 ? (
          <div className="p-6 bg-white/5 border border-white/10 rounded-3xl space-y-4">
            <div className="text-center space-y-1">
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-ninpo-lime">
                Cart is empty
              </p>
              <p className="text-xs text-slate-300 font-bold">
                Personal Shopping & Grocery Delivery
              </p>
            </div>
            {suggestedProducts.length > 0 ? (
              <div className="pt-2 border-t border-white/10 space-y-2">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                  Popular Local Favorites & Grocery Essentials
                </p>
                {suggestedProducts.every(p => p.availableForImmediateAdd === false) && (
                  <div className="px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-200 text-[11px] font-medium leading-snug">
                    🌙 Stores are closed for the night. You can pre-order now for scheduled morning delivery.
                  </div>
                )}
                {suggestedProducts.map(prod => {
                  const prodId = prod.frontendId || prod.id || (prod as { _id?: string })._id || '';
                  const isStoreClosed = prod.availableForImmediateAdd === false;
                  const canAddSuggestion = isStoreClosed ? prod.availableForScheduledAdd !== false : true;
                  return (
                    <div
                      key={prodId}
                      className="p-3 bg-black/40 border border-white/5 hover:border-ninpo-lime/40 rounded-2xl flex items-center justify-between gap-3 transition-all"
                    >
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-white truncate">{prod.name}</p>
                        <p className="text-[10px] text-slate-400 font-medium truncate">{prod.sizeLabel || prod.brand || 'Grocery Favorite'}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => canAddSuggestion && onAddSuggestion(prod)}
                        disabled={!canAddSuggestion}
                        className={`shrink-0 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all ${
                          !canAddSuggestion
                            ? 'bg-white/10 text-slate-400 cursor-not-allowed opacity-60'
                            : isStoreClosed
                              ? 'bg-amber-400/20 text-amber-200 border border-amber-400/30 hover:bg-amber-400/30 active:scale-95'
                              : 'bg-ninpo-lime text-black hover:scale-105 active:scale-95'
                        }`}
                      >
                        {isStoreClosed ? `+ Pre-order ${formatCurrency(prod.price || 0)}` : `+ Add ${formatCurrency(prod.price || 0)}`}
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        ) : (
          availableLineItems.map(li => {
            const product = li.product;
            if (!product) return null;
            const isOutOfStock = (product.stock ?? 0) <= 0;
            const maxStock = product.stock ?? 99;
            const siblingQty = availableLineItems
              .filter((other) => other.productId === li.productId && other.lineId !== li.lineId)
              .reduce((sum, other) => sum + other.quantity, 0);
            const isMaxReached =
              siblingQty + li.quantity >= maxStock || li.quantity >= CART_MAX_QUANTITY_PER_ITEM;

            return (
              <div
                key={li.lineId}
                className={`border rounded-3xl p-4 sm:p-5 space-y-3 transition-all duration-300 ${
                  isOutOfStock
                    ? 'bg-amber-500/5 border-amber-500/30'
                    : 'bg-white/5 border-white/10 hover:border-white/20'
                }`}
              >
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-black/50 border border-white/5 p-1.5 shrink-0 overflow-hidden relative">
                    <img
                      src={(() => {
                        const img = String(product.image || product.imageUrl || '').trim();
                        return img || '/icons/icon-192.png';
                      })()}
                      alt={product.name}
                      className={`w-full h-full object-contain ${isOutOfStock ? 'opacity-30 grayscale' : ''}`}
                      onError={e => {
                        const img = e.currentTarget;
                        img.src = '/icons/icon-192.png';
                      }}
                    />
                    {isOutOfStock && (
                      <span className="absolute inset-0 flex items-center justify-center bg-black/70 text-[8px] font-black uppercase tracking-tighter text-amber-400 text-center px-1">
                        OOS
                      </span>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-black truncate text-sm">{product.name}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className="text-ninpo-lime font-black text-xs tabular-nums">
                        {formatCurrency(li.unitPrice)}
                      </span>
                      {(() => {
                        const badge = resolveTemperatureBadge({
                          temperaturePreference: li.temperaturePreference,
                          isBeverage: li.isBeverage,
                          category: product.category,
                          name: product.name
                        });
                        if (!badge) return null;
                        return badge.kind === 'chilled' ? (
                          <span className="text-[9px] text-sky-300 font-black uppercase tracking-widest bg-sky-500/10 px-1.5 py-0.5 rounded-md border border-sky-500/20">
                            {badge.label}
                          </span>
                        ) : (
                          <span className="text-[9px] text-slate-300 font-black uppercase tracking-widest bg-white/5 px-1.5 py-0.5 rounded-md border border-white/10">
                            {badge.label}
                          </span>
                        );
                      })()}
                      {(() => {
                        const isHeavy = isHeavyProduct(product);
                        const isBulky = isBulkyProduct(product);

                        if (isHeavy) {
                          return (
                            <span className="text-[8px] font-black uppercase tracking-wider text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded-md border border-amber-400/30">
                              🏋️ Heavy
                            </span>
                          );
                        }
                        if (isBulky) {
                          return (
                            <span className="text-[8px] font-black uppercase tracking-wider text-blue-400 bg-blue-400/10 px-1.5 py-0.5 rounded-md border border-blue-400/30">
                              📦 Bulky
                            </span>
                          );
                        }
                        return null;
                      })()}
                      {isOutOfStock ? (
                        <span className="text-[9px] font-black uppercase tracking-wider text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded-full border border-amber-400/30">
                          Out of stock
                        </span>
                      ) : isMaxReached ? (
                        <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 bg-white/5 px-2 py-0.5 rounded-full">
                          Max stock
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className="text-white font-black text-sm tabular-nums">
                      {formatCurrency(li.lineTotal)}
                    </span>
                    <div className="flex items-center gap-1.5 bg-black/40 border border-white/10 rounded-xl p-1">
                      <button
                        type="button"
                        onClick={() => onUpdateQuantity(li.lineId, li.quantity - 1)}
                        aria-label="Decrease quantity"
                        className="w-6 h-6 rounded-lg bg-white/5 hover:bg-white/15 text-slate-300 hover:text-white flex items-center justify-center transition-all active:scale-90"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-6 text-center text-xs font-black text-white tabular-nums">
                        {li.quantity}
                      </span>
                      <button
                        type="button"
                        onClick={() => onUpdateQuantity(li.lineId, li.quantity + 1, maxStock - siblingQty)}
                        disabled={isOutOfStock || isMaxReached}
                        aria-label="Increase quantity"
                        className="w-6 h-6 rounded-lg bg-white/5 hover:bg-white/15 text-slate-300 hover:text-white disabled:opacity-20 disabled:hover:bg-white/5 flex items-center justify-center transition-all active:scale-90"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onRemoveItem(li.lineId)}
                        className="w-6 h-6 rounded-lg text-slate-500 hover:text-ninpo-red hover:bg-ninpo-red/10 flex items-center justify-center transition-all ml-0.5"
                        aria-label="Remove item"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>

                {li.isBeverage && !isOutOfStock ? (
                  <div className="pt-1">
                    <BeverageTemperatureControl
                      size="sm"
                      value={li.temperaturePreference}
                      onChange={next => {
                        onTemperatureChange(li.lineId, next);
                      }}
                      coldDisabled={product.canBeCold === false || product.canBeChilled === false}
                      ambientDisabled={product.canBeAmbient === false}
                    />
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </>
  );
};

export default CartLineList;
